import React, { useRef, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { useInfoCardState } from './useInfoCardState';

// ─── Mode 1: Texture (Z-buffer correct, not interactive) ────────────────────

const InfoCardTexture: React.FC = () => {
    const { selection, cardPos } = useInfoCardState();
    const meshRef = useRef<THREE.Mesh>(null!);
    const texRef = useRef<THREE.CanvasTexture | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        const c = document.createElement('canvas');
        c.width = 512;
        c.height = 320;
        canvasRef.current = c;
        texRef.current = new THREE.CanvasTexture(c);
        texRef.current.minFilter = THREE.LinearFilter;
        texRef.current.magFilter = THREE.LinearFilter;
        return () => { texRef.current?.dispose(); };
    }, []);

    useEffect(() => {
        if (!selection || !canvasRef.current || !texRef.current) return;
        const ctx = canvasRef.current.getContext('2d')!;
        const w = 512, h = 320;
        const r = 16; // corner radius
        const info = selection.info;

        ctx.clearRect(0, 0, w, h);

        // ─── Background with gradient ───
        const bgGrad = ctx.createLinearGradient(0, 0, w, h);
        bgGrad.addColorStop(0, 'rgba(8, 12, 28, 0.95)');
        bgGrad.addColorStop(1, 'rgba(15, 25, 50, 0.92)');
        ctx.beginPath(); ctx.roundRect(0, 0, w, h, r); ctx.fillStyle = bgGrad; ctx.fill();

        // Border
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.3)';
        ctx.lineWidth = 2;
        ctx.stroke();

        // ─── Top accent bar ───
        const accentGrad = ctx.createLinearGradient(0, 0, w, 0);
        accentGrad.addColorStop(0, '#3b82f6');
        accentGrad.addColorStop(0.5, '#8b5cf6');
        accentGrad.addColorStop(1, '#06b6d4');
        ctx.beginPath();
        ctx.roundRect(0, 0, w, 5, [r, r, 0, 0]);
        ctx.fillStyle = accentGrad; ctx.fill();

        // ─── Header: "PARTICLE INFO" ───
        ctx.fillStyle = '#475569';
        ctx.font = '600 13px Inter, system-ui, sans-serif';
        ctx.letterSpacing = '2px';
        ctx.fillText('PARTICLE INFO', 20, 35);

        // Index badge
        const idxText = `#${selection.index}`;
        ctx.font = 'bold 14px Inter, system-ui, sans-serif';
        const idxW = ctx.measureText(idxText).width;
        ctx.fillStyle = 'rgba(59, 130, 246, 0.2)';
        ctx.beginPath(); ctx.roundRect(w - idxW - 30, 18, idxW + 16, 24, 6); ctx.fill();
        ctx.fillStyle = '#60a5fa';
        ctx.fillText(idxText, w - idxW - 22, 35);

        // ─── Filename ───
        ctx.fillStyle = '#f1f5f9';
        ctx.font = 'bold 22px Inter, system-ui, sans-serif';
        const name = info?.filename ?? 'Unknown';
        // Truncate with ellipsis
        let displayName = name;
        if (ctx.measureText(name).width > w - 40) {
            while (ctx.measureText(displayName + '...').width > w - 40 && displayName.length > 0) {
                displayName = displayName.slice(0, -1);
            }
            displayName += '...';
        }
        ctx.fillText(displayName, 20, 72);

        // ─── Divider line ───
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.15)';
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(20, 86); ctx.lineTo(w - 20, 86); ctx.stroke();

        // ─── Info grid (2x2) ───
        const drawInfoBox = (x: number, y: number, label: string, value: string, color: string) => {
            // Box background
            ctx.fillStyle = 'rgba(30, 41, 59, 0.5)';
            ctx.beginPath(); ctx.roundRect(x, y, 220, 52, 8); ctx.fill();
            // Label
            ctx.fillStyle = '#64748b';
            ctx.font = '12px Inter, system-ui, sans-serif';
            ctx.fillText(label, x + 12, y + 20);
            // Value
            ctx.fillStyle = color;
            ctx.font = 'bold 18px Inter, system-ui, sans-serif';
            ctx.fillText(value, x + 12, y + 42);
        };

        if (info) {
            drawInfoBox(20, 100, 'FILE SIZE', `${info.fileSize} KB`, '#60a5fa');
            drawInfoBox(w / 2 + 5, 100, 'TYPE', info.extension, '#a78bfa');
            drawInfoBox(20, 164, 'ASPECT RATIO', info.aspect.toFixed(2), '#34d399');
            drawInfoBox(w / 2 + 5, 164, 'FORMAT', info.format, '#f472b6');
        } else {
            ctx.fillStyle = '#475569';
            ctx.font = '16px Inter, system-ui, sans-serif';
            ctx.fillText('Loading metadata...', 20, 140);
        }

        // ─── Bottom buttons (visual only — texture is not interactive) ───
        const drawBtn = (x: number, label: string, bg: string) => {
            ctx.fillStyle = bg;
            ctx.beginPath(); ctx.roundRect(x, 236, 145, 36, 8); ctx.fill();
            ctx.fillStyle = '#f1f5f9';
            ctx.font = '600 14px Inter, system-ui, sans-serif';
            const tw = ctx.measureText(label).width;
            ctx.fillText(label, x + (145 - tw) / 2, 259);
        };
        drawBtn(20, 'View Details', 'rgba(59, 130, 246, 0.35)');
        drawBtn(w / 2 + 5, 'Open Original', 'rgba(139, 92, 246, 0.35)');

        // ─── Status dot ───
        ctx.fillStyle = '#22c55e';
        ctx.beginPath(); ctx.arc(32, 296, 5, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#64748b';
        ctx.font = '12px Inter, system-ui, sans-serif';
        ctx.fillText('Z-Buffer Active  |  Texture Mode  |  Not Interactive', 44, 300);

        texRef.current.needsUpdate = true;
    }, [selection?.index, selection?.info]);

    // Orient plane along particle normal (same direction as the particle faces)
    const meshQuat = useMemo(() => {
        if (!selection) return new THREE.Quaternion();
        // Plane default normal is (0,0,1). Rotate it to match particle normal.
        const q = new THREE.Quaternion();
        q.setFromUnitVectors(new THREE.Vector3(0, 0, 1), selection.normal);
        return q;
    }, [selection?.index]);

    if (!selection || !texRef.current) return null;

    const worldPos = new THREE.Vector3().addVectors(selection.position, cardPos);

    return (
        <mesh ref={meshRef} position={worldPos} quaternion={meshQuat}>
            <planeGeometry args={[0.16, 0.1]} />
            <meshBasicMaterial
                map={texRef.current}
                transparent
                depthTest={true}
                depthWrite={false}
                side={THREE.DoubleSide}
            />
        </mesh>
    );
};

export default InfoCardTexture;
